import clsx from "clsx"
import Hls from "hls.js"
import Plyr from "plyr"
import "plyr/dist/plyr.css"
import { forwardRef, useEffect, useRef } from "react"

import waitForBytescaleAuthSession from "@utils/waitForBytescaleAuthSession"

import { MS_PER_SECOND } from "@constants/time"

import styles from "./AudioPlayer.module.scss"
import { AudioPlayerProps } from "./AudioPlayer.types"

const HLS_MAX_BUFFER_LENGTH_SECONDS = 30
const HLS_MAX_MAX_BUFFER_LENGTH_SECONDS = 60
const HLS_MANIFEST_TIMEOUT_MS = 5 * MS_PER_SECOND
const MAX_HLS_RETRIES = 3
const PLAYBACK_SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 2]

function AudioPlayer(props: AudioPlayerProps, ref: React.Ref<HTMLDivElement>) {
    const { src, streamingSrc, filename, className } = props

    const audioRef = useRef<HTMLAudioElement>(null)
    const playerRef = useRef<Plyr | null>(null)
    const hlsRef = useRef<Hls | null>(null)

    useEffect(() => {
        const audio = audioRef.current
        if (!audio) {
            return
        }

        const plyrOptions = {
            controls: ["play", "progress", "current-time", "duration", "mute", "volume", "settings"],
            settings: ["speed", "loop"],
            speed: { selected: 1, options: PLAYBACK_SPEED_OPTIONS },
            loop: { active: false },
        }

        let hlsTimeout: ReturnType<typeof setTimeout> | null = null
        let isUnmounted = false

        if (streamingSrc && Hls.isSupported()) {
            const hls = new Hls({
                maxBufferLength: HLS_MAX_BUFFER_LENGTH_SECONDS,
                maxMaxBufferLength: HLS_MAX_MAX_BUFFER_LENGTH_SECONDS,
            })

            hlsRef.current = hls

            let hlsInitialized = false
            let plyrInitialized = false
            let hlsRetryCount = 0

            const fallbackToDirectPlayback = () => {
                if (hlsRef.current) {
                    hlsRef.current.destroy()
                    hlsRef.current = null
                }
                audio.src = src
                if (!plyrInitialized) {
                    plyrInitialized = true
                    playerRef.current = new Plyr(audio, plyrOptions)
                }
            }

            const startStreaming = async () => {
                // On fresh page loads the auth session may still be establishing; starting early
                // would burn the HLS retries on 401s and drop into direct playback
                await waitForBytescaleAuthSession()
                if (isUnmounted) {
                    return
                }

                hls.loadSource(streamingSrc)
                hls.attachMedia(audio)

                hlsTimeout = setTimeout(() => {
                    if (!hlsInitialized) {
                        console.warn("HLS manifest load timeout, falling back to direct playback")
                        fallbackToDirectPlayback()
                    }
                }, HLS_MANIFEST_TIMEOUT_MS)
            }

            void startStreaming()

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                if (hlsTimeout) {
                    clearTimeout(hlsTimeout)
                }
                if (hlsInitialized) {
                    return
                }
                hlsInitialized = true

                playerRef.current = new Plyr(audio, plyrOptions)
                plyrInitialized = true
            })

            hls.on(Hls.Events.ERROR, (_, data) => {
                if (data.fatal) {
                    if (hlsTimeout) {
                        clearTimeout(hlsTimeout)
                    }
                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            hlsRetryCount++
                            if (hlsRetryCount <= MAX_HLS_RETRIES) {
                                hls.startLoad()
                            } else {
                                console.warn("HLS fatal network error, falling back to direct playback:", data.details)
                                fallbackToDirectPlayback()
                            }
                            break
                        case Hls.ErrorTypes.MEDIA_ERROR:
                            hls.recoverMediaError()
                            break
                        default:
                            console.warn("HLS fatal error, falling back to direct playback:", data.details)
                            fallbackToDirectPlayback()
                            break
                    }
                }
            })
        } else if (streamingSrc) {
            // Native HLS (Safari) — browser handles HLS natively; unlike hls.js it won't retry a
            // failed stream, so fall back to the direct file on the first media error
            audio.addEventListener(
                "error",
                () => {
                    audio.src = src
                    audio.load()
                },
                { once: true },
            )
            audio.src = streamingSrc
            playerRef.current = new Plyr(audio, plyrOptions)
        } else {
            // Direct file playback (no streaming URL available)
            audio.src = src
            playerRef.current = new Plyr(audio, plyrOptions)
        }

        return () => {
            isUnmounted = true
            if (hlsTimeout) {
                clearTimeout(hlsTimeout)
            }
            if (hlsRef.current) {
                hlsRef.current.destroy()
                hlsRef.current = null
            }
            if (playerRef.current) {
                playerRef.current.destroy()
                playerRef.current = null
            }
        }
        // Initialize once per mount: Plyr's destroy() replaces the audio element with a detached
        // clone, so a re-run would rebuild the player off-document. A new attachment mounts a
        // fresh player (keyed by attachment id); a re-signed URL for the same file needs no reload.
    }, [])

    return (
        <div ref={ref} className={clsx(styles.container, className)}>
            <audio ref={audioRef} title={filename}>
                Your browser does not support the audio tag.
            </audio>
        </div>
    )
}

export default forwardRef(AudioPlayer)
